import { useState, useEffect } from "react";
import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { CTA } from "../components/Sections";
import { Reveal } from "../components/motion";
import { BrowserMock, PhoneMock } from "../components/Visuals";
import {
  PROJECTS,
  TESTIMONIALS,
  LEGAL_ENTITY,
  SECTOR_LABELS,
  CLIENTS,
} from "../data";
import {
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  ExternalLink,
  Sparkles,
  Layers,
  Cpu,
  Globe,
  Star,
  Check,
  Building2,
  Calendar,
  Zap,
  TrendingUp,
} from "lucide-react";
import { openConsultationModal } from "../components/ConsultationModal";
import api from "../lib/api";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [projectList, setProjectList] = useState(PROJECTS);

  const normalizedSlug = (slug || "").toLowerCase().trim();

  useEffect(() => {
    api
      .get("/projects")
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setProjectList(() => {
            const merged = [...PROJECTS];
            res.data.forEach((p) => {
              const idx = merged.findIndex((m) => m.slug === p.slug);
              if (idx >= 0) merged[idx] = { ...merged[idx], ...p };
              else merged.push(p);
            });
            return merged;
          });
        }
      })
      .catch(() => {});
  }, []);

  // Check static PROJECTS first as the primary single source of truth
  const project =
    PROJECTS.find((p) => p.slug === normalizedSlug) ||
    projectList.find((p) => p.slug === normalizedSlug);

  if (!project) return <Navigate to="/portfolio" replace />;

  const currentIndex = PROJECTS.findIndex((p) => p.slug === project.slug);
  const nextProject =
    PROJECTS[(currentIndex + 1) % PROJECTS.length] || PROJECTS[0];
  const prevProject =
    PROJECTS[(currentIndex - 1 + PROJECTS.length) % PROJECTS.length] ||
    PROJECTS[PROJECTS.length - 1];

  const related = PROJECTS.filter(
    (p) => p.slug !== project.slug && (p.sector === project.sector || p.cat === project.cat)
  ).slice(0, 3);

  // Match client testimonial if available
  const clientReview = TESTIMONIALS.find(
    (t) =>
      t.company.toLowerCase() === project.title.toLowerCase() ||
      t.company.toLowerCase() === (project.domain || "").toLowerCase().replace(/\.[a-z]+$/, "")
  );

  const clientMatch = CLIENTS.find(
    (c) => c.name.toLowerCase() === project.title.toLowerCase()
  );
  const clientLogo = project.logo || clientMatch?.logo;

  const schemaType =
    project.cat === "Mobile" || project.cat === "Web"
      ? "SoftwareApplication"
      : "CreativeWork";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: `${project.title} Case Study`,
    headline: project.tagline
      ? `${project.title}: ${project.tagline}`
      : `${project.title} Case Study`,
    description: project.desc || project.overview,
    url: `https://neuravolt.in/portfolio/${project.slug}`,
    ...(project.img ? { image: project.img } : {}),
    creator: {
      "@type": "Organization",
      name: "NeuraVolt Technology",
      legalName: LEGAL_ENTITY,
      url: "https://neuravolt.in",
    },
    provider: {
      "@type": "Organization",
      name: "NeuraVolt Technology",
      legalName: LEGAL_ENTITY,
      url: "https://neuravolt.in",
    },
    ...(project.sector ? { about: { "@type": "Thing", name: project.sector } } : {}),
    ...(schemaType === "SoftwareApplication" && project.cat
      ? { applicationCategory: `${project.cat}Application` }
      : {}),
    ...(project.tech && project.tech.length > 0
      ? {
          keywords: Array.isArray(project.tech)
            ? project.tech.join(", ")
            : project.tech,
        }
      : {}),
    ...(project.live_url ? { sameAs: project.live_url } : {}),
  };

  const isComingSoon = project.challenge === "Coming Soon";

  return (
    <Layout>
      <Seo
        title={`${project.title} · Case Study`}
        description={project.desc || project.overview}
        path={`/portfolio/${project.slug}`}
        image={project.img}
        jsonLd={jsonLd}
      />

      {/* Top Header & Breadcrumb Ribbon */}
      <section className="pt-28 sm:pt-36 pb-6 px-5 sm:px-8 mesh-dark border-b border-white/5 relative overflow-hidden">
        <div className="blob w-96 h-96 bg-[#FF5A1F] -top-32 -left-32 animate-aurora pointer-events-none" style={{ opacity: 0.2 }} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Breadcrumb Navigation */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-neutral-400 font-mono">
              <Link to="/portfolio" className="hover:text-white transition-colors flex items-center gap-1.5">
                <ArrowLeft className="w-3.5 h-3.5 text-[#FF5A1F]" />
                <span>All Projects</span>
              </Link>
              <span className="text-neutral-600">/</span>
              {project.sector && (
                <>
                  <Link
                    to={`/industries/${project.sector}`}
                    className="hover:text-white transition-colors capitalize text-neutral-400"
                  >
                    {SECTOR_LABELS[project.sector] || project.sector}
                  </Link>
                  <span className="text-neutral-600">/</span>
                </>
              )}
              <span className="text-white font-medium truncate max-w-[200px]">{project.title}</span>
            </div>

            {/* Quick Next Project Switcher */}
            {nextProject && (
              <Link
                to={`/portfolio/${nextProject.slug}`}
                className="inline-flex items-center gap-2 text-xs text-neutral-300 hover:text-[#FF5A1F] transition-colors py-1 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
                title={`Next Case Study: ${nextProject.title}`}
              >
                <span>Next Case Study: <strong className="text-white font-semibold">{nextProject.title}</strong></span>
                <ArrowRight className="w-3.5 h-3.5 text-[#FF5A1F]" />
              </Link>
            )}
          </div>

          {/* Title & Eyebrow */}
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="px-3.5 py-1 rounded-full text-xs font-mono font-bold uppercase tracking-wider bg-[#FF5A1F]/20 text-[#FF5A1F] border border-[#FF5A1F]/40 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                {SECTOR_LABELS[project.sector] || (project.sector ? project.sector.toUpperCase() : project.cat)}
              </span>

              <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                {project.badge || "Verified Client Build"}
              </span>

              {clientLogo && (
                <div className="h-7 px-2.5 py-1 rounded-lg bg-white/10 border border-white/15 flex items-center gap-2 shrink-0">
                  <img
                    src={clientLogo}
                    alt={project.title}
                    className="h-full w-auto object-contain max-w-[80px]"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  <span className="text-[11px] font-mono font-semibold text-neutral-200">{project.title}</span>
                </div>
              )}
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08] mb-4">
              {project.title}
            </h1>

            {project.tagline && (
              <p className="font-heading text-lg sm:text-2xl font-medium text-neutral-300 max-w-4xl leading-snug mb-6">
                {project.tagline}
              </p>
            )}

            <p className="text-neutral-400 text-base sm:text-lg leading-relaxed max-w-3xl">
              {project.desc || project.overview}
            </p>
          </Reveal>

          {/* Metadata Ribbon */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-white/10">
            <div>
              <div className="text-[11px] font-mono text-neutral-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Building2 className="w-3 h-3 text-[#FF5A1F]" /> Client / Product
              </div>
              <div className="font-heading font-semibold text-white text-sm sm:text-base">
                {project.title}
              </div>
            </div>

            <div>
              <div className="text-[11px] font-mono text-neutral-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Globe className="w-3 h-3 text-[#FF5A1F]" /> Industry
              </div>
              <div className="font-heading font-semibold text-white text-sm sm:text-base capitalize">
                {SECTOR_LABELS[project.sector] || project.sector || "Enterprise"}
              </div>
            </div>

            <div>
              <div className="text-[11px] font-mono text-neutral-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Layers className="w-3 h-3 text-[#FF5A1F]" /> Deliverable
              </div>
              <div className="font-heading font-semibold text-white text-sm sm:text-base">
                {project.cat ? `${project.cat} Application` : "Custom Software"}
              </div>
            </div>

            <div className="flex flex-col justify-center">
              {project.domain ? (
                <a
                  href={project.live_url || `https://${project.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#FF5A1F] hover:bg-[#ff6d38] text-white font-semibold text-xs sm:text-sm transition-all shadow-lg shadow-[#FF5A1F]/20 hover:-translate-y-0.5"
                >
                  <span>Visit Live Site</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : (
                <span className="text-xs font-mono text-neutral-400">Enterprise Private</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Showcase Stage */}
      <section className="py-12 sm:py-20 px-5 sm:px-8 mesh-light">
        <div className="max-w-6xl mx-auto space-y-14">
          {/* Framed Device Mockup with Aurora Glow */}
          <Reveal>
            <div className="relative group">
              <div
                className="absolute inset-0 bg-gradient-to-r from-[#FF5A1F]/20 via-orange-500/10 to-transparent blur-3xl -z-10 rounded-3xl"
                style={{ transform: "scale(0.95)" }}
              />

              <div className="rounded-3xl p-2 sm:p-4 bg-[#0B0B0C] border border-black/10 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.35)] overflow-hidden">
                <BrowserMock
                  src={project.img}
                  alt={`${project.title} Platform Interface`}
                  domain={project.domain || "neuravolt.in"}
                  className="aspect-[16/10] w-full"
                />
              </div>
            </div>
          </Reveal>

          {/* Key Proven Results / KPI Grid */}
          {project.metrics && project.metrics.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F]">
                <TrendingUp className="w-4 h-4" />
                <span>Verified Impact & Measurable Outcomes</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {project.metrics.map((m, idx) => (
                  <Reveal key={idx} delay={idx * 0.08}>
                    <div className="h-full rounded-2xl p-6 bg-white border border-black/10 shadow-sm hover:shadow-md hover:border-[#FF5A1F]/40 transition-all flex flex-col justify-between">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/10 text-[#FF5A1F] grid place-items-center font-mono font-bold text-xs">
                          0{idx + 1}
                        </div>
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      </div>
                      <p className="font-heading text-base sm:text-lg font-bold text-neutral-900 leading-snug">
                        {m}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {/* Two-Column Editorial Deep Dive */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Left Column: Narrative (Overview, Challenge, Solution, Features) */}
            <div className="lg:col-span-8 space-y-10">
              {/* Executive Overview */}
              <div className="rounded-3xl p-8 sm:p-10 bg-white border border-black/10 shadow-sm">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] block mb-2">
                  01 / EXECUTIVE CONTEXT
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-neutral-900 mb-4">
                  About the Engagement
                </h2>
                <p className="text-neutral-600 leading-relaxed text-base sm:text-lg font-normal">
                  {project.overview || project.desc}
                </p>
              </div>

              {/* Challenge Card */}
              <div className="rounded-3xl p-8 sm:p-10 bg-white border border-black/10 shadow-sm">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-rose-500 block mb-2">
                  02 / THE ARCHITECTURAL CHALLENGE
                </span>
                <h3 className="font-display text-2xl font-bold text-neutral-900 mb-4">
                  The Problem & Bottlenecks
                </h3>
                <p className="text-neutral-600 leading-relaxed text-base">
                  {project.challenge}
                </p>
              </div>

              {/* Engineering Solution */}
              <div className="rounded-3xl p-8 sm:p-10 bg-[#0B0B0C] text-white border border-white/10 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 bg-[#FF5A1F]/15 blur-3xl pointer-events-none rounded-full" />
                
                <div className="relative z-10">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] block mb-2">
                    03 / THE NEURAVOLT SOLUTION
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4">
                    Engineered for Production Scale
                  </h3>
                  <p className="text-neutral-300 leading-relaxed text-base sm:text-lg mb-8">
                    {project.solution}
                  </p>

                  {/* Results Highlights */}
                  {project.results && project.results.length > 0 && (
                    <div className="pt-6 border-t border-white/10 space-y-3">
                      <div className="text-xs font-mono uppercase tracking-wider text-neutral-400 font-semibold mb-3">
                        Key Deliverables Shipped:
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {project.results.map((r, i) => (
                          <div key={i} className="flex items-start gap-2.5 text-sm text-neutral-200">
                            <span className="w-5 h-5 rounded-full bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center shrink-0 mt-0.5">
                              <Check className="w-3 h-3" />
                            </span>
                            <span>{r}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Sticky Sidebar Specs & Conversion */}
            <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
              {/* Tech Stack Spec Card */}
              <div className="rounded-3xl p-6 sm:p-7 bg-white border border-black/10 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] mb-4">
                  <Cpu className="w-4 h-4" />
                  <span>Technologies Deployed</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(project.tech) ? project.tech : []).map((t) => (
                    <span
                      key={t}
                      className="px-3 py-1.5 rounded-lg text-xs font-mono font-medium bg-neutral-100 text-neutral-800 border border-black/5"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-black/5 space-y-3 text-xs text-neutral-600">
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Architecture:</span>
                    <span className="font-semibold text-neutral-800">Cloud / Microservices</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Security SLA:</span>
                    <span className="font-semibold text-emerald-600">99.99% Uptime Verified</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Engineering:</span>
                    <span className="font-semibold text-neutral-800">NeuraVolt Core Team</span>
                  </div>
                </div>
              </div>

              {/* Client Review (if matched) */}
              {clientReview && (
                <div className="rounded-3xl p-6 sm:p-7 bg-gradient-to-br from-neutral-900 to-[#0B0B0C] text-white border border-white/10 shadow-xl">
                  <div className="flex items-center gap-1 mb-3 text-[#FF5A1F]">
                    {Array.from({ length: clientReview.rating || 5 }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>

                  <p className="text-xs sm:text-sm text-neutral-200 italic leading-relaxed mb-4">
                    "{clientReview.quote}"
                  </p>

                  <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                    <div className="w-9 h-9 rounded-full bg-[#FF5A1F]/20 text-[#FF5A1F] font-bold text-xs grid place-items-center uppercase border border-[#FF5A1F]/40 shrink-0">
                      {clientReview.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-xs sm:text-sm text-white">
                        {clientReview.name}
                      </div>
                      <div className="text-[11px] text-neutral-400">
                        {clientReview.role}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Direct Consultation Action Card */}
              <div className="rounded-3xl p-7 bg-[#FF5A1F] text-white shadow-xl shadow-[#FF5A1F]/25 flex flex-col justify-between">
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-white/80 font-bold mb-2 flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5" /> Start Your Project
                  </div>
                  <h4 className="font-display text-xl font-bold mb-2">
                    Want to build a platform like {project.title}?
                  </h4>
                  <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-6">
                    Our senior engineering leads will evaluate your architecture, scoping, and roadmap on a 30-minute discovery call.
                  </p>
                </div>

                <button
                  onClick={() => openConsultationModal(`Similar architecture to ${project.title}`)}
                  className="w-full py-3.5 px-5 rounded-2xl bg-white text-[#0B0B0B] font-semibold text-xs sm:text-sm hover:bg-neutral-100 transition-all shadow-md inline-flex items-center justify-center gap-2"
                >
                  <span>Schedule Technical Discovery</span>
                  <ArrowRight className="w-4 h-4 text-[#FF5A1F]" />
                </button>
              </div>
            </div>
          </div>

          {/* Related Case Studies Grid */}
          {related.length > 0 && (
            <div className="pt-16 border-t border-black/10">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] block mb-1">
                    PORTFOLIO ARCHIVES
                  </span>
                  <h3 className="font-display text-2xl font-bold text-neutral-900">
                    Explore Related Case Studies
                  </h3>
                </div>

                <Link
                  to="/portfolio"
                  className="text-xs font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1"
                >
                  <span>View All Projects</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/portfolio/${p.slug}`}
                    className="group block rounded-2xl overflow-hidden bg-white border border-black/10 shadow-sm hover:shadow-xl hover:border-[#FF5A1F]/40 transition-all p-4"
                  >
                    <div className="rounded-xl overflow-hidden aspect-[16/10] bg-neutral-100 mb-4">
                      <img
                        src={p.img}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h4 className="font-heading font-bold text-base text-neutral-900 group-hover:text-[#FF5A1F] transition-colors">
                        {p.title}
                      </h4>
                      <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-[#FF5A1F] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                    <p className="text-xs text-neutral-500 line-clamp-2">
                      {p.tagline || p.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <CTA />
    </Layout>
  );
}
